import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { IZVORI, AKCIJE, PODRAZUMEVANO } from './pretragaKonfig';

/* Podešavanja glavne pretrage — lična, po operateru.

   Dva pitanja, tim redom, jer drugo zavisi od prvog:
     1. ŠTA se pretražuje (izvori)
     2. ŠTA SE DEŠAVA kad rezultat izabereš (po jedna radnja za svaki izvor)

   Odeljak sa radnjama se iscrtava samo za UKLJUČENE izvore — spisak radnji za
   izvor koji se ne pretražuje je mrtvo slovo i samo produžava dijalog.

   Izvor je KARTICA sa obojenom ivicom, a ne tačka pa spisak: sa četiri izvora
   jedan za drugim nije se videlo gde jedan prestaje a drugi počinje, pa su
   radnje delovale kao jedna duga lista sa slučajno raspoređenim tačkama.

   Radio je NAŠ, ne PrimeReact-ov: cela vrsta je meta klika (lakše se pogađa od
   16px kružića) i selekcija se boji bojom svog izvora, pa se izbor vidi i
   perifernim pogledom. Ispod je pravi `<input type="radio">` — tastatura,
   strelice i čitači ekrana rade sami od sebe.

   Izmene se drže u lokalnom stanju i primenjuju tek na „Sačuvaj". Pretraga
   koja se menja pod rukom dok se dijalog gleda čini podešavanje neproverivim. */

const DialogPodesavanjaPretrage = ({ vidljiv, podesavanja, cuva, onSacuvaj, onZatvori }) => {
    const { t } = useTranslation();
    const [izbor, setIzbor] = useState(podesavanja || PODRAZUMEVANO);

    /* Pri svakom otvaranju kreće se od onoga što je sačuvano — dijalog
       zatvoren bez čuvanja ne sme da ostavi trag u sledećem otvaranju. */
    useEffect(() => {
        if (vidljiv) setIzbor(podesavanja || PODRAZUMEVANO);
    }, [vidljiv, podesavanja]);

    const opcijeIzvora = IZVORI.map(i => ({ value: i.kljuc, label: t(i.naziv), boja: i.boja }));
    const ukljuceni = IZVORI.filter(i => izbor.izvori.includes(i.kljuc));

    const postaviAkciju = (izvor, kljuc) =>
        setIzbor(p => ({ ...p, akcije: { ...p.akcije, [izvor]: kljuc } }));

    /* Čip nosi boju svog izvora — istu koju ta grupa ima u rezultatima i koju
       njen pin ima na mapi. Bez toga su čipovi četiri jednaka siva
       pravougaonika i ne kažu ništa dok se ne pročitaju. */
    const sablonCipa = (v) => {
        const o = opcijeIzvora.find(x => x.value === v);
        if (!o) return v;
        return (
            <span className="gps-pod-cip" style={{ '--cip-boja': o.boja }}>
                <span className="gps-pod-tacka" />
                {o.label}
            </span>
        );
    };

    const sablonOpcije = (o) => (
        <span className="gps-pod-opcija">
            <span className="gps-pod-tacka" style={{ '--cip-boja': o.boja }} />
            {o.label}
        </span>
    );

    const podnozje = (
        <div className="gps-pod-podnozje">
            <Button
                type="button"
                icon="pi pi-replay"
                label={t('gps.pretragaVratiPodrazumevano')}
                className="p-button-text p-button-sm"
                onClick={() => setIzbor(PODRAZUMEVANO)}
            />
            <Button
                type="button"
                icon="pi pi-save"
                label={t('common.sacuvaj')}
                severity="success"
                loading={cuva}
                disabled={!izbor.izvori.length}
                onClick={() => onSacuvaj(izbor)}
            />
        </div>
    );

    return (
        <Dialog
            header={t('gps.pretragaPodesavanjaNaslov')}
            visible={vidljiv}
            onHide={onZatvori}
            className="gps-dijalog-podesavanja"
            style={{ width: '34rem', maxWidth: '95vw' }}
            /* Visina se ograničava ovde, a ne prepušta sadržaju: sa sva četiri
               izvora spisak preraste ekran i poslednja vrsta završi iza
               podnožja — tako je i izgledalo pre. */
            contentStyle={{ maxHeight: '62vh', overflowY: 'auto' }}
            footer={podnozje}
            draggable={false}
            dismissableMask
        >
            <div className="gps-pod-odeljak">
                <label className="gps-pod-naslov" htmlFor="gps-izvori">
                    {t('gps.pretragaPrema')} <span className="gps-pod-obavezno">*</span>
                </label>
                <p className="gps-pod-pomoc">{t('gps.pretragaPremaOpis')}</p>

                <MultiSelect
                    inputId="gps-izvori"
                    value={izbor.izvori}
                    options={opcijeIzvora}
                    onChange={(e) => setIzbor(p => ({ ...p, izvori: e.value }))}
                    display="chip"
                    selectedItemTemplate={sablonCipa}
                    itemTemplate={sablonOpcije}
                    placeholder={t('gps.pretragaIzaberiIzvore')}
                    showClear
                    className="gps-pod-multi"
                    style={{ width: '100%' }}
                />
                {!izbor.izvori.length && (
                    <small className="gps-pod-greska">{t('gps.pretragaBarJedan')}</small>
                )}
            </div>

            {!!ukljuceni.length && (
                <>
                    <div className="gps-pod-razdelnik">
                        <span>{t('gps.pretragaNaIzbor')}</span>
                    </div>

                    {ukljuceni.map(i => (
                        <div key={i.kljuc} className="gps-pod-kartica" style={{ '--izvor-boja': i.boja }}>
                            <div className="gps-pod-kartica-vrh">
                                <span className="gps-pod-tacka" style={{ '--cip-boja': i.boja }} />
                                {t(i.naziv)}
                            </div>

                            <div className="gps-pod-radio-grupa" role="radiogroup" aria-label={t(i.naziv)}>
                                {AKCIJE[i.kljuc].map(a => {
                                    const id = `akcija-${i.kljuc}-${a.kljuc}`;
                                    const izabrana = izbor.akcije[i.kljuc] === a.kljuc;
                                    return (
                                        <label
                                            key={a.kljuc}
                                            htmlFor={id}
                                            className={`gps-pod-radio${izabrana ? ' gps-pod-radio-izabran' : ''}${a.uPripremi ? ' gps-pod-radio-uskoro' : ''}`}
                                        >
                                            <input
                                                id={id}
                                                type="radio"
                                                name={`akcija-${i.kljuc}`}
                                                value={a.kljuc}
                                                disabled={a.uPripremi}
                                                checked={izabrana}
                                                onChange={() => postaviAkciju(i.kljuc, a.kljuc)}
                                            />
                                            <span className="gps-pod-radio-tekst">{t(a.naziv)}</span>
                                            {/* Stavka koja tek dolazi se vidi, ali se ne bira —
                                                korisnik pita gde je, pa je poštenije prikazati
                                                je ugašenu nego sakriti. */}
                                            {a.uPripremi && <span className="gps-pod-uskoro">{t('gps.uPripremi')}</span>}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </Dialog>
    );
};

export default DialogPodesavanjaPretrage;
